import React, { useCallback, useMemo, useState } from 'react';
import { GECKO_CONSTS, GeckoFields, GeckoModal, GeckoModalService } from 'ui';
import {
  useGeckoIntegrations,
  useGeckoIntegrationsFunction,
} from 'services/gecko-integrations';
import GeckoAuth from 'services/gecko-auth';
import GeckoErrorsAlert from 'components/GeckoErrorsAlert';
import ToggleButtons from '../../components/FieldToggleButton';
import { useFieldMapping } from './FieldMapping';
import { validateKey } from 'utilities/fieldKeyUtils.js';

const { INTEGRATION } = require('chat-constants');

const moduleToOption = (module) => {
  const { handle, label } = module;
  return {
    label,
    value: handle,
  };
};

const modulesToOptions = (modules) => {
  return modules.map(moduleToOption);
};

const isOptionsFieldType = (fieldType) =>
  [
    GECKO_CONSTS.FIELD_TYPE_SELECT,
    GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
  ].includes(fieldType);

const MappingModal = ({
  permittedSecondaryModuleHandles,
  excludedSecondaryModuleHandles,
  mappedField,
  integrationId,
  primaryConnectionType,
  secondaryConnectionType,
  primaryAttributes,
  primaryModule,
}) => {
  const [secondaryModule, setSecondaryModule] = useState(
    mappedField?.secondaryAttribute?.module || ''
  );
  const [secondaryAttributeId, setSecondaryAttributeId] = useState(
    mappedField?.secondaryAttribute?.id || ''
  );
  const [useExistingPrimaryAttribute, setUseExistingPrimaryAttribute] =
    useState(!!mappedField);
  const [existingPrimaryAttributeId, setExistingPrimaryAttributeId] = useState(
    mappedField?.primaryAttribute?.id || ''
  );
  const [newPrimaryAttributeType, setNewPrimaryAttributeType] = useState('');
  const [newPrimaryAttributeKey, setNewPrimaryAttributeKey] = useState('');
  const [newPrimaryAttributeOptions, setNewPrimaryAttributeOptions] =
    useState('');
  const [submissionErrors, setSubmissionErrors] = useState([]);

  const GeckoIntegrations = useGeckoIntegrations({
    getAuthTokenCallback: GeckoAuth.getToken,
  });

  const FieldMapping = useFieldMapping();

  const getSecondaryModules = useCallback(() => {
    return GeckoIntegrations.getSecondaryModules(integrationId);
  }, [GeckoIntegrations, integrationId]);

  const {
    data: allSecondaryModules,
    isLoading: isLoadingSecondaryModules,
    errors: secondaryModuleErrors,
  } = useGeckoIntegrationsFunction(getSecondaryModules, { initialValue: [] });

  const isForSalesforceConversationSetup = useMemo(() => {
    return (
      secondaryConnectionType === INTEGRATION.SALESFORCE &&
      primaryModule === INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION
    );
  }, [secondaryConnectionType, primaryModule]);

  const secondaryModulesReadyForFiltering = useMemo(() => {
    return (
      permittedSecondaryModuleHandles ===
        Object(permittedSecondaryModuleHandles) &&
      Array.isArray(
        permittedSecondaryModuleHandles[INTEGRATION.MODULE_HANDLE_CHAT_CONTACT]
      ) &&
      Array.isArray(
        permittedSecondaryModuleHandles[INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION]
      ) &&
      !isLoadingSecondaryModules &&
      Array.isArray(allSecondaryModules) &&
      allSecondaryModules.length > 0 &&
      typeof excludedSecondaryModuleHandles === 'object' &&
      Array.isArray(excludedSecondaryModuleHandles[INTEGRATION.MODULE_HANDLE_CHAT_CONTACT]) &&
      Array.isArray(
        excludedSecondaryModuleHandles[INTEGRATION.MODULE_HANDLE_CHAT_CONVERSATION]
      )
    );
  }, [
    permittedSecondaryModuleHandles,
    isLoadingSecondaryModules,
    allSecondaryModules,
    excludedSecondaryModuleHandles,
  ]);

  const secondaryModules = useMemo(() => {
    if (secondaryModulesReadyForFiltering) {
      return allSecondaryModules.filter((module) =>
        permittedSecondaryModuleHandles[primaryModule].length
          ? permittedSecondaryModuleHandles[primaryModule].includes(
              module.handle
            )
          : !excludedSecondaryModuleHandles[primaryModule].includes(
              module.handle
            )
      );
    }
    return allSecondaryModules;
  }, [
    primaryModule,
    secondaryModulesReadyForFiltering,
    allSecondaryModules,
    permittedSecondaryModuleHandles,
    excludedSecondaryModuleHandles,
  ]);

  const getSecondaryAttributes = useCallback(async () => {
    if (!secondaryModule) {
      return {
        success: true,
        data: [],
        errors: [],
      };
    }
    return GeckoIntegrations.getSecondaryAttributes(
      integrationId,
      secondaryModule
    );
  }, [GeckoIntegrations, integrationId, secondaryModule]);

  const {
    data: secondaryAttributes,
    isLoading: isLoadingSecondaryAttributes,
    errors: secondaryAttributesErrors,
  } = useGeckoIntegrationsFunction(getSecondaryAttributes, {
    initialValue: [],
  });

  const secondaryModuleField = useMemo(() => {
    return {
      key: INTEGRATION.SECONDARY_MODULE,
      label: `${secondaryConnectionType} module`,
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      options: modulesToOptions(secondaryModules),
      config: {
        isLoading: isLoadingSecondaryModules,
      },
      width: '100%',
    };
  }, [secondaryConnectionType, secondaryModules, isLoadingSecondaryModules]);

  const secondaryAttributeIdField = useMemo(() => {
    return {
      key: INTEGRATION.SECONDARY_ATTRIBUTE_ID,
      label: `${secondaryConnectionType} field`,
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      options: secondaryAttributes.map((attribute) => {
        const { id, label } = attribute;
        return {
          label: label,
          value: id,
        };
      }),
      config: {
        isLoading: isLoadingSecondaryAttributes,
      },
      width: '100%',
    };
  }, [
    secondaryConnectionType,
    secondaryAttributes,
    isLoadingSecondaryAttributes,
  ]);

  const toggleUseExistingPrimaryAttributeField = useMemo(() => {
    return {
      key: INTEGRATION.USE_EXISTING_PRIMARY_ATTRIBUTE,
      label: ``,
      type: GECKO_CONSTS.FIELD_TYPE_CUSTOM,
      width: '100%',
      component: <ToggleButtons />,
      hideWhen: () => isForSalesforceConversationSetup,
    };
  }, [isForSalesforceConversationSetup]);

  const existingPrimaryAttributeIdField = useMemo(() => {
    return {
      key: INTEGRATION.EXISTING_PRIMARY_ATTRIBUTE_ID,
      label: `${primaryConnectionType} field`,
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      options: primaryAttributes.map((attribute) => {
        const { id, config } = attribute;
        return {
          label: config?.label || id,
          value: id,
        };
      }),
      width: '100%',
      hideWhen: () =>
        !isForSalesforceConversationSetup && !useExistingPrimaryAttribute,
    };
  }, [
    primaryConnectionType,
    primaryAttributes,
    useExistingPrimaryAttribute,
    isForSalesforceConversationSetup,
  ]);

  const newPrimaryAttributeKeyField = useMemo(() => {
    return {
      key: INTEGRATION.NEW_PRIMARY_ATTRIBUTE_KEY,
      label: 'Key',
      description:
        'A unique name that will distinguish this field in the database from every other field. You cannot use spaces, capital letters or special characters.',
      type: GECKO_CONSTS.FIELD_TYPE_TEXT,
      width: '100%',
      required: true,
      hideWhen: () =>
        isForSalesforceConversationSetup || useExistingPrimaryAttribute,
    };
  }, [useExistingPrimaryAttribute, isForSalesforceConversationSetup]);

  const newPrimaryAttributeTypeField = useMemo(() => {
    return {
      key: INTEGRATION.NEW_PRIMARY_ATTRIBUTE_TYPE,
      label: 'Type',
      type: GECKO_CONSTS.FIELD_TYPE_SELECT,
      options: [
        {
          label: 'Text (Single Line)',
          value: GECKO_CONSTS.FIELD_TYPE_TEXT,
        },
        {
          label: 'Text (Multiple Lines)',
          value: GECKO_CONSTS.FIELD_TYPE_TEXTAREA,
        },
        {
          label: 'Email',
          value: GECKO_CONSTS.FIELD_TYPE_EMAIL,
        },
        {
          label: 'Date',
          value: GECKO_CONSTS.FIELD_TYPE_DATE,
        },
        {
          label: 'Telephone Number',
          value: GECKO_CONSTS.FIELD_TYPE_TELEPHONE,
        },
        {
          label: 'Number',
          value: GECKO_CONSTS.FIELD_TYPE_NUMBER,
        },
        {
          label: 'Single Choice',
          value: GECKO_CONSTS.FIELD_TYPE_SELECT,
        },
        {
          label: 'Multiple Choice',
          value: GECKO_CONSTS.FIELD_TYPE_SELECT_MULTI,
        },
      ],
      width: '100%',
      hideWhen: () =>
        isForSalesforceConversationSetup || useExistingPrimaryAttribute,
    };
  }, [useExistingPrimaryAttribute, isForSalesforceConversationSetup]);

  const newPrimaryAttributeOptionsField = useMemo(() => {
    return {
      key: INTEGRATION.NEW_PRIMARY_ATTRIBUTE_OPTIONS,
      label: 'Field options in text format',
      type: GECKO_CONSTS.FIELD_TYPE_TEXTAREA,
      width: '100%',
      hideWhen: () =>
        isForSalesforceConversationSetup ||
        useExistingPrimaryAttribute ||
        !isOptionsFieldType(newPrimaryAttributeType),
    };
  }, [
    useExistingPrimaryAttribute,
    newPrimaryAttributeType,
    isForSalesforceConversationSetup,
  ]);

  const newPrimaryAttributeOptionsLegendField = useMemo(() => {
    return {
      key: INTEGRATION.NEW_PRIMARY_ATTRIBUTE_OPTIONS_LEGEND,
      type: GECKO_CONSTS.FIELD_TYPE_DISPLAY,
      width: '100%',
      hideWhen: () =>
        isForSalesforceConversationSetup ||
        useExistingPrimaryAttribute ||
        !isOptionsFieldType(newPrimaryAttributeType),
      render: () => {
        return (
          <span>
            <p>Put each of your options on a new line.</p>
            <p>
              If you need your option to have a different value for your
              integration(s), write your option like:
            </p>
            <code>Label|value</code>
          </span>
        );
      },
    };
  }, [
    useExistingPrimaryAttribute,
    newPrimaryAttributeType,
    isForSalesforceConversationSetup,
  ]);

  const fields = useMemo(() => {
    return [
      secondaryModuleField,
      secondaryAttributeIdField,
      toggleUseExistingPrimaryAttributeField,
      existingPrimaryAttributeIdField,
      newPrimaryAttributeKeyField,
      newPrimaryAttributeTypeField,
      newPrimaryAttributeOptionsField,
      newPrimaryAttributeOptionsLegendField,
    ];
  }, [
    secondaryModuleField,
    secondaryAttributeIdField,
    toggleUseExistingPrimaryAttributeField,
    existingPrimaryAttributeIdField,
    newPrimaryAttributeKeyField,
    newPrimaryAttributeTypeField,
    newPrimaryAttributeOptionsField,
    newPrimaryAttributeOptionsLegendField,
  ]);

  const values = useMemo(() => {
    return {
      secondaryModule,
      secondaryAttributeId,
      useExistingPrimaryAttribute,
      existingPrimaryAttributeId,
      newPrimaryAttributeType,
      newPrimaryAttributeKey,
      newPrimaryAttributeOptions,
    };
  }, [
    secondaryModule,
    secondaryAttributeId,
    useExistingPrimaryAttribute,
    existingPrimaryAttributeId,
    newPrimaryAttributeType,
    newPrimaryAttributeKey,
    newPrimaryAttributeOptions,
  ]);

  const errors = useMemo(() => {
    return [
      ...secondaryModuleErrors,
      ...secondaryAttributesErrors,
      ...submissionErrors,
    ];
  }, [secondaryModuleErrors, secondaryAttributesErrors, submissionErrors]);

  const findSecondaryAttribute = (
    secondaryAttributes,
    secondaryAttributeId
  ) => {
    let secondaryAttribute = null;
    if (Array.isArray(secondaryAttributes) && secondaryAttributeId) {
      secondaryAttribute = secondaryAttributes.find(
        ({ id }) => id === secondaryAttributeId
      );
    }
    return secondaryAttribute || null;
  };

  const selectedSecondaryAttribute = useMemo(() => {
    return findSecondaryAttribute(secondaryAttributes, secondaryAttributeId);
  }, [secondaryAttributes, secondaryAttributeId]);

  const secondaryAttributeLabel = selectedSecondaryAttribute?.label;

  const existingPrimaryAttribute = useMemo(() => {
    let existingPrimaryAttribute = null;
    if (existingPrimaryAttributeId) {
      existingPrimaryAttribute = primaryAttributes.find(
        ({ id }) => id === existingPrimaryAttributeId
      );
    }
    return existingPrimaryAttribute;
  }, [primaryAttributes, existingPrimaryAttributeId]);

  const newPrimaryAttribute = useMemo(() => {
    let newPrimaryAttribute = null;
    if (
      primaryModule &&
      secondaryAttributeLabel &&
      newPrimaryAttributeType &&
      newPrimaryAttributeKey
    ) {
      newPrimaryAttribute = {
        module: primaryModule,
        label: secondaryAttributeLabel,
        type: newPrimaryAttributeType,
        key: newPrimaryAttributeKey,
        options: newPrimaryAttributeOptions,
        config: { label: secondaryAttributeLabel },
      };
    }
    return newPrimaryAttribute;
  }, [
    primaryModule,
    secondaryAttributeLabel,
    newPrimaryAttributeType,
    newPrimaryAttributeOptions,
    newPrimaryAttributeKey,
  ]);

  const makeOptionsString = (attribute) => {
    let options = '';
    if (
      attribute.config &&
      attribute.config.options &&
      Array.isArray(attribute.config.options)
    ) {
      options = attribute.config.options
        .map((option) => {
          return option.label + '|' + option.value;
        })
        .join('\n');
    }
    return options;
  };

  const handleSecondaryModuleUpdate = (value) => {
    setSecondaryAttributeId('');
    setNewPrimaryAttributeOptions('');
    return setSecondaryModule(value);
  };

  const handleSecondaryAttributeIdUpdate = useCallback(
    (value, newPrimaryAttributeType, secondaryAttributes) => {
      if (isOptionsFieldType(newPrimaryAttributeType)) {
        const secondaryAttribute = findSecondaryAttribute(
          secondaryAttributes,
          value
        );
        setNewPrimaryAttributeOptions(makeOptionsString(secondaryAttribute));
      } else {
        setNewPrimaryAttributeOptions('');
      }
      return setSecondaryAttributeId(value);
    },
    []
  );

  const handleUseExistingPrimaryAttributeUpdate = (value, mappedField) => {
    setNewPrimaryAttributeOptions('');
    setNewPrimaryAttributeType('');
    setNewPrimaryAttributeKey('');

    if (value) {
      setExistingPrimaryAttributeId(mappedField?.primaryAttribute?.id || '');
    } else {
      setExistingPrimaryAttributeId('');
    }

    return setUseExistingPrimaryAttribute(value);
  };

  const handlePrimaryAttributeTypeUpdate = useCallback(
    (value, selectedSecondaryAttribute) => {
      if (isOptionsFieldType(value) && selectedSecondaryAttribute) {
        setNewPrimaryAttributeOptions(
          makeOptionsString(selectedSecondaryAttribute)
        );
      } else {
        setNewPrimaryAttributeOptions('');
      }
      return setNewPrimaryAttributeType(value);
    },
    []
  );

  const fieldsHandler = useCallback(
    (key, value) => {
      return {
        [INTEGRATION.EXISTING_PRIMARY_ATTRIBUTE_ID]: (value) =>
          setExistingPrimaryAttributeId(value),
        [INTEGRATION.SECONDARY_MODULE]: (value) =>
          handleSecondaryModuleUpdate(value),
        [INTEGRATION.SECONDARY_ATTRIBUTE_ID]: (value) =>
          handleSecondaryAttributeIdUpdate(
            value,
            newPrimaryAttributeType,
            secondaryAttributes
          ),
        [INTEGRATION.USE_EXISTING_PRIMARY_ATTRIBUTE]: (value) =>
          handleUseExistingPrimaryAttributeUpdate(value, mappedField),
        [INTEGRATION.NEW_PRIMARY_ATTRIBUTE_TYPE]: (value) =>
          handlePrimaryAttributeTypeUpdate(value, selectedSecondaryAttribute),
        [INTEGRATION.NEW_PRIMARY_ATTRIBUTE_KEY]: (value) =>
          setNewPrimaryAttributeKey(value),
        [INTEGRATION.NEW_PRIMARY_ATTRIBUTE_OPTIONS]: (value) =>
          setNewPrimaryAttributeOptions(value),
      }[key](value);
    },
    [
      selectedSecondaryAttribute,
      mappedField,
      newPrimaryAttributeType,
      secondaryAttributes,
      handlePrimaryAttributeTypeUpdate,
      handleSecondaryAttributeIdUpdate,
    ]
  );

  const createNewPrimaryAttributeIfRequired = useCallback(async () => {
    if (existingPrimaryAttribute) {
      return Promise.resolve(existingPrimaryAttribute.id);
    }
    // Check key contains only numbers and/or lower case letters
    if (!validateKey(newPrimaryAttribute.key)) {
      return Promise.reject({
        errors: [
          {
            code: 500,
            title: 'Invalid Key',
            detail:
              'You cannot use spaces, capital letters or special characters',
          },
        ],
      });
    } else {
      const result = await FieldMapping.createField(newPrimaryAttribute);
      if (result.success === false) {
        return Promise.reject(result);
      }
      return result.id;
    }
  }, [existingPrimaryAttribute, newPrimaryAttribute, FieldMapping]);

  const createOrUpdateFieldMapping = useCallback(
    (primaryAttributeId) => {
      return mappedField
        ? GeckoIntegrations.updateFieldMapping(
            integrationId,
            useExistingPrimaryAttribute
              ? existingPrimaryAttribute
              : Object.assign(newPrimaryAttribute, {
                  id: primaryAttributeId,
                }),
            selectedSecondaryAttribute,
            mappedField.fieldId
          )
        : GeckoIntegrations.createFieldMapping(
            integrationId,
            existingPrimaryAttribute ||
              Object.assign(newPrimaryAttribute, {
                id: primaryAttributeId,
              }),
            selectedSecondaryAttribute
          );
    },
    [
      GeckoIntegrations,
      integrationId,
      existingPrimaryAttribute,
      newPrimaryAttribute,
      selectedSecondaryAttribute,
      useExistingPrimaryAttribute,
      mappedField,
    ]
  );

  const mappedFieldConfigurationHasChanged = useMemo(() => {
    return (
      !useExistingPrimaryAttribute ||
      existingPrimaryAttribute === null ||
      selectedSecondaryAttribute === null ||
      JSON.stringify(existingPrimaryAttribute) !==
        JSON.stringify(mappedField?.primaryAttribute) ||
      JSON.stringify(selectedSecondaryAttribute) !==
        JSON.stringify(mappedField?.secondaryAttribute)
    );
  }, [
    existingPrimaryAttribute,
    mappedField,
    useExistingPrimaryAttribute,
    selectedSecondaryAttribute,
  ]);

  const noSecondaryAttributeSelected = useMemo(() => {
    return selectedSecondaryAttribute === null;
  }, [selectedSecondaryAttribute]);

  const updateMappedFieldConfigurationIsIncomplete = useMemo(() => {
    return (
      !mappedFieldConfigurationHasChanged ||
      noSecondaryAttributeSelected ||
      !(
        (!useExistingPrimaryAttribute && newPrimaryAttribute) ||
        (useExistingPrimaryAttribute && existingPrimaryAttribute)
      )
    );
  }, [
    useExistingPrimaryAttribute,
    newPrimaryAttribute,
    existingPrimaryAttribute,
    mappedFieldConfigurationHasChanged,
    noSecondaryAttributeSelected,
  ]);

  const createMappedFieldConfigurationIsIncomplete = useMemo(() => {
    return !(
      selectedSecondaryAttribute &&
      (existingPrimaryAttribute || newPrimaryAttribute)
    );
  }, [
    selectedSecondaryAttribute,
    existingPrimaryAttribute,
    newPrimaryAttribute,
  ]);

  const modalButtons = useMemo(() => {
    return [
      {
        name: 'Create mapping',
        type: GECKO_CONSTS.BTN_TYPE_PRIMARY,
        icon: 'far fa-save',
        disabledWhen: () => {
          return mappedField
            ? updateMappedFieldConfigurationIsIncomplete
            : createMappedFieldConfigurationIsIncomplete;
        },
        handler: () => {
          createNewPrimaryAttributeIfRequired()
            .then((primaryAttributeId) => {
              return createOrUpdateFieldMapping(primaryAttributeId).then(
                (result) => {
                  if (result.success === false) {
                    return setSubmissionErrors(result.errors);
                  }
                  new GeckoModalService().close(result.data);
                }
              );
            })
            .catch((errorResponse) => {
              return setSubmissionErrors(
                errorResponse?.errors || [
                  {
                    code: 500,
                    title: 'Could not create new field',
                    detail: errorResponse?.raw || 'An error occurred',
                  },
                ]
              );
            });
        },
      },
    ];
  }, [
    createNewPrimaryAttributeIfRequired,
    createOrUpdateFieldMapping,
    updateMappedFieldConfigurationIsIncomplete,
    mappedField,
    createMappedFieldConfigurationIsIncomplete,
  ]);

  return (
    <GeckoModal name="Map field" btns={modalButtons}>
      <form>
        <GeckoErrorsAlert errors={errors} />
        <GeckoFields fields={fields} values={values} handler={fieldsHandler} />
      </form>
    </GeckoModal>
  );
};

export default MappingModal;
